import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useMessageStyle } from "./ChatItem.helper";
import { AttachmentUtil, FileUtil, StorageUtil } from "utils";
import { AppConstant, LangConstant, SystemConstant } from "const";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { ForwardingIcon } from "components/icons";
import { FolderOutlined, DownloadOutlined, Refresh } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { getInteractor } from "services/local.service";
import FileAvatar from "components/avatar/FileAvatar";
import { useEffect, useRef, useState } from "react";
import { useCleanUpEffect, useDownloadFile } from "hooks";
import { SCROLL_TRIGGER_CLASS } from "../useQueryMessage";
import { useSelector } from "react-redux";
import { AttachmentSelectors } from "redux-store";
import { useAlertContext } from "components/context/AlertContext";

const FileBox = ({
  mediaData,
  isMine = false,
  isShowOptMenu = false,
  disablePadding = false,
  groupId,
  onForward,
  onRetry,
  isCurrentDevice = false,
}) => {
  const classes = useStyles();
  const messengerClasses = useMessageStyle();
  const statusRef = useRef();
  const { t: getLabel } = useTranslation(LangConstant.NS_HOME_CONVERSATION);
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const { downloadFile, openFolder } = useDownloadFile();
  const { isMounted } = useCleanUpEffect();
  const { showAlert } = useAlertContext();

  const [fileAvatarUrl, setFileAvatarUrl] = useState("");
  const [uploadStatus, setUploadStatus] = useState();
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [lastUploaded, setLastUploaded] = useState(0);
  const [lastTimestamp, setLastTimestamp] = useState(Date.now());

  const attachment = useSelector(state => AttachmentSelectors.getAttachmentById(state, mediaData.attachmentId));

  const handleDownloadFile = () => {
    if (isUploadSuccess) {
      downloadFile([mediaData], groupId);
    } else {
      showAlert({ content: getLabel(LangConstant.TXT_FILE_NOT_FOUND), alertProps: { severity: "error" } });
    }
  };

  const handleOpenFileFolder = () => openFolder(mediaData);

  useEffect(() => {
    statusRef.current = mediaData.status;
    if (mediaData.status !== undefined) {
      setUploadStatus(mediaData.status);
    }
    if (mediaData.filePath) {
      setFileAvatarUrl(mediaData.filePath);
    } else if (mediaData.attachmentId) {
      getInteractor(prefixKey)
        .LocalFileService.get(mediaData.attachmentId)
        .then(fileRecord => {
          if (fileRecord) {
            const url = AttachmentUtil.getLocalPath(fileRecord.url, mediaData.metaData.fileName);
            if (isMounted()) setFileAvatarUrl(url);
          }
        });
    }
  }, [mediaData]);

  useEffect(() => {
    const checkStatusTimeout = setTimeout(() => {
      if (statusRef.current === SystemConstant.UPLOAD_STATUS.inProgress) {
        setUploadStatus(SystemConstant.UPLOAD_STATUS.failed);
        getInteractor(prefixKey).LocalFileService.updateFileFail(mediaData.attachmentId);
      }
    }, AppConstant.CHECK_UPLOAD_TIMEOUT);

    return () => clearTimeout(checkStatusTimeout);
  }, []);

  const [isUploading, setIsUploading] = useState(false);
  const [isUploadFailed, setIsUploadFailed] = useState(false);
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);
  const [statusLabel, setStatusLabel] = useState("");

  useEffect(() => {
    let label = "";
    if (uploadStatus === SystemConstant.UPLOAD_STATUS.downloadFailed) {
      label = getLabel(LangConstant.TXT_DOWNLOAD_FILE_FAIL);
    } else if (uploadStatus === SystemConstant.UPLOAD_STATUS.decryptFailed) {
      label = getLabel(LangConstant.TXT_DECRYPT_FILE_FAIL);
    } else if (uploadStatus === SystemConstant.UPLOAD_STATUS.failed) {
      label = getLabel(LangConstant.TXT_SEND_FAILED);
      // logic hiện tại sẽ không còn file upload failed được gửi qua msg,
      // nên nếu nhận được file với bất cứ trạng thái nào thì vẫn cho phép tải lại
      if (!isCurrentDevice) {
        setUploadStatus(SystemConstant.UPLOAD_STATUS.downloadFailed);
      }
    }
    setStatusLabel(label);
  }, [uploadStatus, getLabel]);
  useEffect(() => {
    // Cập nhật trạng thái isUploading
    setIsUploading(uploadStatus === SystemConstant.UPLOAD_STATUS.inProgress);

    // Cập nhật trạng thái isUploadFailed
    setIsUploadFailed(
      uploadStatus === SystemConstant.UPLOAD_STATUS.failed ||
        uploadStatus === SystemConstant.UPLOAD_STATUS.downloadFailed ||
        uploadStatus === SystemConstant.UPLOAD_STATUS.decryptFailed,
    );

    // Cập nhật trạng thái isUploadSuccess
    setIsUploadSuccess(uploadStatus === SystemConstant.UPLOAD_STATUS.success);
  }, [uploadStatus]);
  const progress = attachment?.progress ? ((attachment.progress / attachment.total) * 100).toFixed(2) : 0;

  useEffect(() => {
    if (isUploading && attachment?.progress) {
      const now = Date.now();
      const bytesSinceLast = attachment.progress - lastUploaded;
      const timeElapsed = (now - lastTimestamp) / 1000; // Convert to seconds
      const speed = bytesSinceLast / timeElapsed; // Bytes per second

      setUploadSpeed(speed);
      setLastUploaded(attachment.progress);
      setLastTimestamp(now);
    }
  }, [attachment?.progress, isUploading]);

  const updateStatus = uploadStatus => {
    setUploadStatus(uploadStatus);
    getInteractor(prefixKey).LocalFileService.updateFileStatus(mediaData.attachmentId, uploadStatus);
  };

  useEffect(() => {
    if (attachment?.fileOperation === SystemConstant.FILE_OPERATION.downloadFailed) {
      updateStatus(SystemConstant.UPLOAD_STATUS.downloadFailed);
    } else if (attachment?.fileOperation === SystemConstant.FILE_OPERATION.decryptFailed) {
      updateStatus(SystemConstant.UPLOAD_STATUS.decryptFailed);
    }
  }, [attachment]);

  const retry = () => {
    getInteractor(prefixKey).LocalFileService.updateFileStatus(
      mediaData.attachmentId,
      SystemConstant.UPLOAD_STATUS.success,
    );
    setUploadStatus(SystemConstant.UPLOAD_STATUS.inProgress);
    onRetry();
  };

  return (
    <Box className={classes.fullWidthBox}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        className={clsx(
          messengerClasses.messengerRoot,
          !disablePadding && messengerClasses.spacing,
          classes.contentContainer,
          isMine && messengerClasses.myMessage,
          SCROLL_TRIGGER_CLASS,
        )}
        style={{
          width:
            uploadStatus === SystemConstant.UPLOAD_STATUS.decryptFailed ||
            uploadStatus === SystemConstant.UPLOAD_STATUS.downloadFailed
              ? "calc(100% - 70px)" // Giảm 70px chiều rộng
              : "100%",
        }}
      >
        <Stack direction="row" alignItems="center" width="100%">
          <FileAvatar url={fileAvatarUrl} file={mediaData} progress={progress} loading={isUploading} />

          <Box flex={1}>
            <Typography variant="subtitle2" color="inherit" className="ellipsis">
              {mediaData.metaData.fileName}
            </Typography>

            {isUploadFailed ? (
              <Typography variant="caption" color="error" className="ellipsis">
                {statusLabel}
              </Typography>
            ) : (
              <Typography variant="caption" color="inherit" className="ellipsis">
                {FileUtil.convertFileSizeUnit(mediaData.metaData.size)}
              </Typography>
            )}

            {isUploading && (
              <Typography variant="caption" color="inherit">
                {attachment?.fileOperation === SystemConstant.FILE_OPERATION.downloading
                  ? getLabel(LangConstant.TXT_DOWNLOADING)
                  : attachment?.fileOperation === SystemConstant.FILE_OPERATION.uploading
                    ? getLabel(LangConstant.TXT_SENDING)
                    : ""}
                {` ${FileUtil.convertFileSizeUnit(attachment?.progress)}/`}
                <Typography variant="caption" style={{ color: "#30CC45" }}>
                  {FileUtil.convertFileSizeUnit(attachment?.total)}
                </Typography>
                {` - ${FileUtil.convertFileSizeUnit(uploadSpeed)}/s`}
              </Typography>
            )}
          </Box>

          {isShowOptMenu && isUploadSuccess && (
            <Stack direction="row" className={classes.groupIconList}>
              <IconButton color={isMine ? "inherit" : "#000000"} onClick={handleOpenFileFolder}>
                <FolderOutlined />
              </IconButton>
              <IconButton color={isMine ? "inherit" : "#000000"} onClick={onForward}>
                <ForwardingIcon />
              </IconButton>
              <IconButton color={isMine ? "inherit" : "#000000"} onClick={handleDownloadFile}>
                <DownloadOutlined />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </Stack>
      {(uploadStatus === SystemConstant.UPLOAD_STATUS.decryptFailed ||
        uploadStatus === SystemConstant.UPLOAD_STATUS.downloadFailed) && (
        <Stack
          direction="row"
          alignItems="center"
          className={classes.retryButton}
          style={{ right: "0px" }} // Đặt cạnh Stack đầu tiên
        >
          <Box className={classes.chatReSend} onClick={() => retry()}>
            <IconButton color="primary" className={classes.chatReSendButton}>
              <Refresh style={{ fontSize: 12 }} />
            </IconButton>
            <Typography color="primary" className={classes.chatReSendText}>
              {getLabel(LangConstant.TXT_RETRY)}
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default FileBox;

const useStyles = makeStyles(theme => ({
  contentContainer: {
    width: "100%",
    borderRadius: 12,
    paddingTop: 8,
    paddingBottom: 8,

    "&:hover $groupIconList": {
      display: "flex",
    },
  },

  filePreviewIcon: {
    width: 48,
    height: 48,
    backgroundColor: theme.palette.white,
  },

  groupIconList: {
    display: "none",
  },

  forwardingIcon: {
    width: 24,
    height: 24,
  },

  disableStyle: {
    cursor: "default",
    opacity: 0.5,
    "&:hover $groupIconList": {
      display: "none",
    },
  },
  fullWidthBox: {
    width: "100%",
    position: "relative",
  },

  retryButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
  },

  chatReSend: {
    display: "flex",
    alignItems: "center",
    width: "fit-content",
    height: 20,
  },
  chatReSendButton: {
    padding: 0,
    lineHeight: "20px",
    marginRight: 2,
  },
}));
